import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BackLink from "@/components/BackLink.jsx";
import { useAuth } from "@/auth/AuthContext.jsx";
import { login } from "@/api/public.api.js";
import {
    ADMIN_PROFILE_ROUTE,
    MAIN_ROUTE,
    ROLES
} from "@/utils/constants.js";
import { ADMIN_PROFILE_DEFAULT_SECTION } from "@/utils/profileMenus.js";
import { isStaffRole, STAFF_ROLE_LABELS } from "@/utils/staffAccess.js";
import "@/components/login-modal.css";
import "./admin-login.css";

export default function AdminLoginPage() {
    const [showPassword, setShowPassword] = useState(false);
    const [form, setForm] = useState({ username: "", password: "", role: ROLES.ADMIN });
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const { isAuthenticated, role, login: authLogin } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (isAuthenticated && isStaffRole(role)) {
            navigate(`${ADMIN_PROFILE_ROUTE}?section=${ADMIN_PROFILE_DEFAULT_SECTION}`, { replace: true });
        }
    }, [isAuthenticated, role, navigate]);

    const handleChange = (event) => {
        setForm({ ...form, [event.target.name]: event.target.value });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (submitting) {
            return;
        }
        setError("");
        setSubmitting(true);
        try {
            const data = await login({
                username: form.username,
                password: form.password,
                role: form.role
            });
            authLogin(form.role, data.token);
            navigate(`${ADMIN_PROFILE_ROUTE}?section=${ADMIN_PROFILE_DEFAULT_SECTION}`, { replace: true });
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (isAuthenticated && role === ROLES.CUSTOMER) {
        return (
            <div className="admin-login-page">
                <div className="admin-login-card card">
                    <h1>Espace personnel</h1>
                    <p className="muted">
                        Vous êtes connecté en tant que client. Déconnectez-vous pour accéder à l&apos;espace personnel.
                    </p>
                    <Link to={MAIN_ROUTE} className="btn btn-primary btn-inline">
                        Retour au catalogue
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-login-page">
            <div className="admin-login-card card">
                <h1>Espace personnel</h1>
                <p className="muted admin-login-intro">
                    Connexion réservée au personnel (administrateur, manager, secrétaire).
                </p>
                <form onSubmit={handleSubmit}>
                    <label className="field">
                        Rôle
                        <select name="role" value={form.role} onChange={handleChange} required>
                            <option value={ROLES.ADMIN}>{STAFF_ROLE_LABELS[ROLES.ADMIN]}</option>
                            <option value={ROLES.MANAGER}>{STAFF_ROLE_LABELS[ROLES.MANAGER]}</option>
                            <option value={ROLES.SECRETARY}>{STAFF_ROLE_LABELS[ROLES.SECRETARY]}</option>
                        </select>
                    </label>
                    <label className="field">
                        Nom d&apos;utilisateur
                        <input
                            name="username"
                            type="text"
                            value={form.username}
                            onChange={handleChange}
                            minLength={3}
                            maxLength={50}
                            required
                            autoComplete="username"
                        />
                    </label>
                    <label className="field">
                        Mot de passe
                        <div className="password-field">
                            <input
                                name="password"
                                type={showPassword ? "text" : "password"}
                                value={form.password}
                                onChange={handleChange}
                                minLength={3}
                                maxLength={50}
                                required
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? "Masquer" : "Afficher"}
                            </button>
                        </div>
                    </label>
                    {error && <p className="error">{error}</p>}
                    <button type="submit" className="btn btn-primary full-width" disabled={submitting}>
                        {submitting ? "Connexion..." : "Se connecter"}
                    </button>
                </form>
                <p className="admin-login-footer muted">
                    <BackLink to={MAIN_ROUTE}>Retour au catalogue</BackLink>
                </p>
            </div>
        </div>
    );
}
